import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import SettingButton from "../../components/settings/SettingButton";
import SettingButtonSwitch from "../../components/settings/SettingButtonSwitch";
import { useLogOutUser } from "../../domains/user/hooks/useLogOutUser";
import {
  disableHapticStore,
  disableSoundStore,
} from "../../domains/user/stores/nativeSettingsStore";
import { useStore } from "../../lib/store";
import { apiClient } from "../../services/api";
import { gatEventUserDeleteAccount } from "../../tools/analytics/domains/user.analytics";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import BellIcon from "../../assets/icons/bell.svg?react";
import { IconMail } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { usePushNotificationSwitch } from "../../domains/notification/hooks/usePushNotificationSwitch";

export default function SettingsEditAccountPage() {
  const logout = useLogOutUser();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [disableSound, setDisableSound] = useStore(disableSoundStore);
  const [disableHaptic, setDisableHaptic] = useStore(disableHapticStore);
  const [isDeleteAccountModalVisible, setDeleteAccountModalVisible] =
    useState(false);
  const [isDeletingAccount, setDeletingAccount] = useState(false);
  // The switch, the foreground re-read and the six outcomes of one tap all live
  // in the hook. This page used to carry its own copy, which is how the two
  // settings surfaces came to disagree; the copy also predated the account-level
  // opt-out, so it showed a switch reading ON to a reader who receives nothing.
  const push = usePushNotificationSwitch();

  function handleUpdateSound() {
    setDisableSound(!disableSound);
  }

  function handleUpdateHaptic() {
    setDisableHaptic(!disableHaptic);
  }

  async function handleDeleteAccount() {
    try {
      if (currentUser === null) return;

      setDeletingAccount(true);
      await apiClient.delete("/webapp/private/users/me", {});
      gatEventUserDeleteAccount(currentUser.uid);
      displayToast(t("Account deleted"), "success");

      logout({ skipApiCalls: true });
    } catch {
      setDeletingAccount(false);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent title={t("Manage account")} />}
    >
      <div className="box-border h-full pb-[72px] flex flex-col">
        <div className="p-[30px]" />

        <div className="flex-1 flex flex-col gap-2">
          <div className="font-dm-sans text-2xl font-bold leading-[28.8px] tracking-tight px-8 py-4">
            {t("Notifications & Communications")}
          </div>

          {/* A signpost only while the OS still owns the tap — it has not been
              asked, or it refused, and its own screen is the only place that
              changes either. Once it has granted permission the tap decides the
              account, and a row promising a trip to the device settings would
              then silence push everywhere instead. */}
          {push.isNative && (push.needsOsPrompt || push.denied) ? (
            <SettingButton icon={<BellIcon />} onClick={push.toggle}>
              <div className="flex flex-col">
                <span>{t("Push notifications")}</span>
                <span className="text-xs text-gray-500">
                  {push.needsOsPrompt
                    ? t("Allow notifications")
                    : t("Manage in device settings")}
                </span>
              </div>
            </SettingButton>
          ) : (
            <SettingButtonSwitch
              icon={<BellIcon />}
              checked={push.enabled}
              onClick={push.toggle}
            >
              {push.denied
                ? t("Notifications blocked")
                : t("Push notifications")}
            </SettingButtonSwitch>
          )}

          {currentUser && (
            <SettingButtonSwitch
              icon={<IconMail size={22} />}
              checked={!currentUser.emailOptOut}
              onClick={() => {
                const next = !currentUser.emailOptOut;
                void apiClient
                  .patch("/webapp/private/users/me", {
                    json: { emailOptOut: next },
                  })
                  .then(() => {
                    reloadCurrentUser();
                  })
                  .catch(console.error);
              }}
            >
              {t("Email communications")}
            </SettingButtonSwitch>
          )}

          <div className="font-dm-sans text-2xl font-bold leading-[28.8px] tracking-tight px-8 py-4 pt-8">
            {t("In-App")}
          </div>

          <SettingButtonSwitch
            iconName="volume"
            iconScale={1.1}
            checked={!disableSound}
            onClick={handleUpdateSound}
          >
            {t("Sound effects")}
          </SettingButtonSwitch>

          <SettingButtonSwitch
            iconName="haptic"
            iconScale={1.1}
            checked={!disableHaptic}
            onClick={handleUpdateHaptic}
          >
            {t("Haptic effects")}
          </SettingButtonSwitch>

          <div className="font-dm-sans text-2xl font-bold leading-[28.8px] tracking-tight px-8 py-4 pt-8">
            {t("Account")}
          </div>

          <SettingButton
            iconName="delete_account"
            onClick={() => {
              setDeleteAccountModalVisible(true);
            }}
          >
            {t("Delete profile")}
          </SettingButton>

          <div className="pb-8" />
        </div>
      </div>

      <DeleteAccountModal
        isVisible={isDeleteAccountModalVisible}
        onClose={() => {
          setDeleteAccountModalVisible(false);
        }}
        onDeleteAccount={handleDeleteAccount}
        isDeletingAccount={isDeletingAccount}
      />
    </ScrollableLayout>
  );
}

function DeleteAccountModal({
  isVisible,
  onClose,
  onDeleteAccount,
  isDeletingAccount,
}: {
  isVisible: boolean;
  onClose: () => void;
  onDeleteAccount: () => void;
  isDeletingAccount: boolean;
}) {
  const { t } = useTranslation();

  function handleOutsideClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  if (!isVisible) return null;

  return (
    <div
      className="fixed z-999 inset-0 bg-black/75 flex justify-center items-center"
      onClick={handleOutsideClick}
    >
      <div className="flex flex-col items-center w-[70%] bg-white rounded-2xl p-6 text-center shadow-[0px_4px_10px_rgba(0,0,0,0.1)]">
        <h2 className="font-dm-sans text-lg font-semibold text-black m-0 mb-[5px]">
          {t("Second bite at the cherry 🍒")}
        </h2>
        <h3 className="font-dm-sans text-sm font-normal text-black m-0 mb-5">
          {t("Do you really want to permanently delete your account?")}
        </h3>
        <Button
          variant="primary"
          size="lg"
          onClick={onClose}
          className="w-[60%] mb-4"
        >
          {t("Cancel")}
        </Button>
        <Button
          variant="destructive"
          size="lg"
          className="w-[60%]"
          onClick={onDeleteAccount}
          disabled={isDeletingAccount}
        >
          {t("Yes")}
        </Button>
      </div>
    </div>
  );
}
